import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/client';

interface Company {
  id: string;
  name: string;
}

interface Campaign {
  id: string;
  name: string;
  submitted_at?: string | null;
}

interface GroupRow {
  id?: string;
  group_key: string;
  group_name: string;
  hypothesis: string;
  campaign_ids: string[];
  is_active: boolean;
}

type ReportType = 'product_discovery' | 'lead_generation';

function formatSubmitted(ts: string | null | undefined): string {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const REPORT_TYPES: { value: ReportType; label: string; icon: string }[] = [
  { value: 'product_discovery', label: 'Product Discovery', icon: '🔬' },
  { value: 'lead_generation', label: 'Lead Generation', icon: '🎯' },
];

const CampaignGroupConfig: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [reportType, setReportType] = useState<ReportType>('product_discovery');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Load companies with running campaigns assigned to this CSR
  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch companies where assigned_csr_id = current user
      const { data } = await supabase
        .from('companies')
        .select('id, name')
        .eq('assigned_csr_id', user.id)
        .order('name');

      setCompanies(data || []);
    };
    load();
  }, []);

  const loadData = useCallback(async () => {
    if (!selectedCompanyId) return;
    setLoading(true);
    setSaveMessage(null);

    // Load running campaigns for company
    const { data: campData } = await supabase
      .from('campaigns')
      .select('id, name, submitted_at')
      .eq('company_id', selectedCompanyId)
      .eq('status', 'running')
      .order('name');

    setCampaigns(campData || []);

    // Load existing group configs
    const { data: configData } = await supabase
      .from('campaign_group_configs')
      .select('id, group_key, group_name, hypothesis, is_active')
      .eq('company_id', selectedCompanyId)
      .eq('report_type', reportType)
      .order('group_key');

    if (!configData || configData.length === 0) {
      setGroups([]);
      setLoading(false);
      return;
    }

    // Load members for each config
    const configIds = configData.map(c => c.id);
    const { data: memberData } = await supabase
      .from('campaign_group_config_members')
      .select('config_id, campaign_id')
      .in('config_id', configIds);

    const memberMap: Record<string, string[]> = {};
    (memberData || []).forEach((m: any) => {
      if (!memberMap[m.config_id]) memberMap[m.config_id] = [];
      memberMap[m.config_id].push(m.campaign_id);
    });

    setGroups(configData.map(c => ({
      id: c.id,
      group_key: c.group_key,
      group_name: c.group_name,
      hypothesis: c.hypothesis ?? '',
      is_active: c.is_active,
      campaign_ids: memberMap[c.id] || [],
    })));

    setLoading(false);
  }, [selectedCompanyId, reportType]);

  useEffect(() => { loadData(); }, [loadData]);

  const addGroup = () => {
    const nextKey = reportType === 'product_discovery'
      ? `C${groups.length + 1}`
      : `S${groups.length + 1}`;
    setGroups(prev => [...prev, { group_key: nextKey, group_name: '', hypothesis: '', campaign_ids: [], is_active: true }]);
  };

  const removeGroup = (idx: number) => {
    setGroups(prev => prev.filter((_, i) => i !== idx));
  };

  const updateGroup = (idx: number, field: keyof GroupRow, value: any) => {
    setGroups(prev => prev.map((g, i) => i === idx ? { ...g, [field]: value } : g));
  };

  const toggleCampaign = (groupIdx: number, campaignId: string) => {
    setGroups(prev => prev.map((g, i) => {
      if (i !== groupIdx) return g;
      const ids = g.campaign_ids.includes(campaignId)
        ? g.campaign_ids.filter(id => id !== campaignId)
        : [...g.campaign_ids, campaignId];
      return { ...g, campaign_ids: ids };
    }));
  };

  const handleSave = async () => {
    if (!selectedCompanyId) return;
    setSaving(true);
    setSaveMessage(null);

    const { data: { user } } = await supabase.auth.getUser();

    try {
      for (const group of groups) {
        if (!group.group_key.trim() || !group.group_name.trim()) continue;

        if (group.id) {
          // Update existing config
          await supabase
            .from('campaign_group_configs')
            .update({
              group_key: group.group_key.trim(),
              group_name: group.group_name.trim(),
              hypothesis: group.hypothesis.trim() || null,
              is_active: group.is_active,
            })
            .eq('id', group.id);

          // Replace members
          await supabase
            .from('campaign_group_config_members')
            .delete()
            .eq('config_id', group.id);

          if (group.campaign_ids.length > 0) {
            await supabase
              .from('campaign_group_config_members')
              .insert(group.campaign_ids.map(cid => ({ config_id: group.id, campaign_id: cid })));
          }
        } else {
          // Insert new config
          const { data: newConfig, error } = await supabase
            .from('campaign_group_configs')
            .insert({
              company_id: selectedCompanyId,
              report_type: reportType,
              group_key: group.group_key.trim(),
              group_name: group.group_name.trim(),
              hypothesis: group.hypothesis.trim() || null,
              is_active: group.is_active,
              created_by: user?.id,
            })
            .select('id')
            .single();

          if (error) throw error;

          if (newConfig && group.campaign_ids.length > 0) {
            await supabase
              .from('campaign_group_config_members')
              .insert(group.campaign_ids.map(cid => ({ config_id: newConfig.id, campaign_id: cid })));
          }
        }
      }

      setSaveMessage({ type: 'success', text: 'Groups saved successfully.' });
      loadData();
    } catch (err: any) {
      setSaveMessage({ type: 'error', text: `Failed to save: ${err.message}` });
    }

    setSaving(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Campaign Group Setup</h1>
        <p className="text-sm text-gray-500 mt-1">
          Define which campaigns are grouped together for weekly reports. The cron job reads this configuration every Sunday.
        </p>
      </div>

      {/* Company selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
        <select
          value={selectedCompanyId}
          onChange={e => setSelectedCompanyId(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="">Select a company...</option>
          {companies.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {selectedCompanyId && (
        <>
          {/* Report type tabs */}
          <div className="flex gap-2 mb-6">
            {REPORT_TYPES.map(rt => (
              <button
                key={rt.value}
                onClick={() => setReportType(rt.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  reportType === rt.value
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {rt.icon} {rt.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-sm text-gray-500 py-8 text-center">Loading...</div>
          ) : (
            <>
              {campaigns.length === 0 && (
                <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  No running campaigns found for this company. Campaigns must have status = 'running' to appear here.
                </div>
              )}

              {/* Groups */}
              <div className="space-y-4 mb-6">
                {groups.length === 0 && (
                  <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-400">
                    No groups configured yet. Click "Add Group" to get started.
                  </div>
                )}

                {groups.map((group, idx) => (
                  <div
                    key={idx}
                    className={`border rounded-xl p-4 ${group.is_active ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <input
                        type="text"
                        value={group.group_key}
                        onChange={e => updateGroup(idx, 'group_key', e.target.value)}
                        placeholder="Key (e.g. C1)"
                        className="w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-mono focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        value={group.group_name}
                        onChange={e => updateGroup(idx, 'group_name', e.target.value)}
                        placeholder="Group name (e.g. Creative Directors NYC)"
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={group.is_active}
                          onChange={e => updateGroup(idx, 'is_active', e.target.checked)}
                          className="rounded"
                        />
                        Active
                      </label>
                      <button
                        onClick={() => removeGroup(idx)}
                        className="text-red-400 hover:text-red-600 text-xs px-2 py-1"
                      >
                        Remove
                      </button>
                    </div>

                    {/* Hypothesis — Product Discovery only */}
                    {reportType === 'product_discovery' && (
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Hypothesis <span className="text-gray-400 font-normal">(the assumption being tested)</span>
                        </label>
                        <textarea
                          value={group.hypothesis}
                          onChange={e => updateGroup(idx, 'hypothesis', e.target.value)}
                          placeholder="e.g. Creative Directors in London are open to AI tools for post-production if framed around time savings rather than job displacement."
                          rows={2}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                        />
                      </div>
                    )}

                    {/* Campaign checkboxes */}
                    {campaigns.length > 0 ? (
                      <div className="grid grid-cols-1 gap-1 pl-1">
                        <p className="text-xs text-gray-500 mb-1">Campaigns in this group:</p>
                        {campaigns.map(camp => (
                          <label key={camp.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={group.campaign_ids.includes(camp.id)}
                              onChange={() => toggleCampaign(idx, camp.id)}
                              className="rounded"
                            />
                            <span className="text-gray-700">
                              {camp.name || <span className="italic text-gray-400">unnamed</span>}
                              {camp.submitted_at && (
                                <span className="text-gray-400 font-normal"> (submitted {formatSubmitted(camp.submitted_at)})</span>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 pl-1">No running campaigns available to assign.</p>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-4">
                <button
                  onClick={addGroup}
                  className="px-4 py-2 border border-purple-300 text-purple-700 text-sm font-medium rounded-lg hover:bg-purple-50 transition-colors"
                >
                  + Add Group
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || groups.length === 0}
                  className="px-5 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Groups'}
                </button>
                {saveMessage && (
                  <span className={`text-sm ${saveMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                    {saveMessage.text}
                  </span>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default CampaignGroupConfig;
